import React, { createContext, useContext, useState } from 'react';
import './Loading.css';

// Loading Context
const LoadingContext = createContext(null);

/**
 * Loading Provider 组件
 * 提供全局 Loading 状态管理
 */
export const LoadingProvider = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState(new Map());

  // 显示 Loading
  const showLoading = (key = 'global', message = '加载中...') => {
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      newStates.set(key, { message, timestamp: Date.now() });
      return newStates;
    });
  };

  // 隐藏 Loading
  const hideLoading = (key = 'global') => {
    setLoadingStates(prev => {
      const newStates = new Map(prev);
      newStates.delete(key);
      return newStates;
    });
  };

  // 隐藏所有 Loading
  const hideAllLoading = () => {
    setLoadingStates(new Map());
  };

  // 检查是否正在加载
  const isLoading = (key = 'global') => {
    return loadingStates.has(key);
  };

  // 获取全局 Loading 状态
  const globalLoading = loadingStates.get('global');

  return (
    <LoadingContext.Provider value={{ 
      showLoading, 
      hideLoading, 
      hideAllLoading, 
      isLoading,
      loadingStates 
    }}>
      {children}
      {globalLoading && (
        <LoadingOverlay message={globalLoading.message} />
      )}
    </LoadingContext.Provider>
  );
};

/**
 * Loading Overlay 组件
 * 全屏加载遮罩
 */
const LoadingOverlay = ({ message }) => {
  return (
    <div className="loading-overlay">
      <div className="loading-content">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        {message && <div className="loading-message">{message}</div>}
      </div>
    </div>
  );
};

/**
 * Loading 组件
 * 局部加载指示器
 */
const Loading = ({ 
  size = 'medium',  // small, medium, large
  message = '',
  fullscreen = false,
  className = ''
}) => {
  if (fullscreen) {
    return <LoadingOverlay message={message} />;
  }

  return (
    <div className={`loading-inline loading-${size} ${className}`}>
      <div className="loading-spinner-inline">
        <div className="spinner-ring"></div>
        <div className="spinner-ring"></div>
        <div className="spinner-ring"></div>
      </div>
      {message && <div className="loading-message-inline">{message}</div>}
    </div>
  );
};

/**
 * useLoading Hook
 * 在组件中使用 Loading 功能
 */
export const useLoading = (key = 'global') => {
  const context = useContext(LoadingContext);
  
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }

  const { showLoading, hideLoading, isLoading } = context;

  return {
    showLoading: (message) => showLoading(key, message),
    hideLoading: () => hideLoading(key),
    isLoading: isLoading(key)
  };
};

/**
 * withLoading HOC
 * 为异步操作添加 Loading 效果
 */
export const withLoading = (asyncFn, key = 'global', message = '加载中...') => {
  return async (...args) => {
    const { showLoading, hideLoading } = useLoading(key);
    
    try {
      showLoading(message);
      const result = await asyncFn(...args);
      return result;
    } finally {
      hideLoading();
    }
  };
};

export default Loading;

